import { useEffect, useCallback, useState } from 'react';
import { useList } from '@refinedev/core';

import type { Tag } from '../types';

export const useTagSearch = (open: boolean, search: string, page: number, pageSize: number) => {
  const [allOptions, setAllOptions] = useState<Tag[]>([]);

  const {
    result: data,

    query: { isLoading, isFetching, refetch }
  } = useList<Tag>({
    resource: 'tags',
    pagination: { currentPage: page, pageSize: pageSize },
    filters: search
      ? [
          {
            field: 'search',
            operator: 'contains',
            value: search
          }
        ]
      : undefined,
    queryOptions: {
      enabled: open,
      queryKey: [search, page]
    }
  });

  const mergeUniqueTags = useCallback((prevTags: Tag[], newTags: Tag[], isFirstPage: boolean) => {
    const combinedTags = isFirstPage ? newTags : [...prevTags, ...newTags];
    const uniqueTagsMap = new Map<string | number, Tag>();
    combinedTags.forEach(tag => uniqueTagsMap.set(tag.id, tag));
    return Array.from(uniqueTagsMap.values());
  }, []);

  const [prevData, setPrevData] = useState(data);
  const [prevOpen, setPrevOpen] = useState(open);

  if (prevData !== data) {
    setPrevData(data);
    if (data && open) {
      setAllOptions(prev => mergeUniqueTags(prev, data.data, page === 1));
    }
  }

  if (prevOpen !== open) {
    setPrevOpen(open);
    if (!open) {
      setAllOptions([]);
    }
  }

  useEffect(() => {
    if (!open) {
      refetch();
    }
  }, [open, refetch]);

  const clearAll = useCallback(() => {
    setAllOptions([]);
  }, []);

  return {
    allOptions,
    isLoading,
    isFetching,
    data,
    clearAll
  };
};
